/**
 * Menu Silver — interactive behaviour (dropdowns, accordion, mobile menu, active links).
 * Bundled into dist/bundle.min.js via Vite's import.meta.glob in templates/main.js.
 * CSS lives in menu_silver.css — no runtime style injection here.
 */

function normalizePath(p) {
    return (p || '').split('?')[0].replace(/\/+$/, '') || '/';
}

const currentPath = normalizePath(window.location.pathname);

function isActiveItem(itemPath) {
    const clean = normalizePath(itemPath);
    if (currentPath === clean) return true;
    if (currentPath === '/' || clean === '/') return false;
    return currentPath.startsWith(clean + '/');
}

function isExactMatch(itemPath) {
    return currentPath === normalizePath(itemPath);
}

/* ── Active link marking ── */
function markActive() {
    document.querySelectorAll('[data-ms-header] a[href]').forEach(a => {
        if (a.hasAttribute('data-ms-exact')) return; // exact-only links handled below
        if (isActiveItem(a.getAttribute('href'))) a.classList.add('ms-active');
    });

    document.querySelectorAll('[data-ms-path]').forEach(el => {
        if (isActiveItem(el.dataset.msPath)) el.classList.add('ms-active');
    });

    document.querySelectorAll('[data-ms-exact]').forEach(el => {
        const path = el.getAttribute('href') || '';
        if (path && isExactMatch(path)) el.classList.add('ms-exact-active');
    });

    // Parent-active: scan all nav item wrappers (unified + legacy)
    document.querySelectorAll('[data-ms-nav-item], [data-ms-dropdown], [data-ms-accordion]').forEach(wrap => {
        const children = wrap.querySelectorAll('a[href], [data-ms-path]');
        const hasActive = Array.from(children).some(el => {
            const p = el.getAttribute('href') || el.dataset.msPath || '';
            return p && isActiveItem(p);
        });
        if (hasActive) wrap.classList.add('ms-parent-active');
    });
}

/* ── Close sibling top-level nav items (desktop only) ── */
function closeOtherTopLevel(current) {
    document.querySelectorAll('.ms-unified-nav-items > [data-ms-nav-item].ms-open').forEach(el => {
        if (el === current) return;
        el.classList.remove('ms-open');
        el.querySelector(':scope > [data-ms-nav-trigger]')?.setAttribute('aria-expanded', 'false');
    });
}

/* ── Unified nav items (replaces separate initDropdowns + initAccordions) ── */
function initNavItems() {
    const header = document.querySelector('[data-ms-header]');
    if (!header || header.hasAttribute('data-ms-nav-inited')) return;
    header.setAttribute('data-ms-nav-inited', '');
    // Hover — per-element, only for items already in DOM at init time
    document.querySelectorAll('[data-ms-nav-item]').forEach(item => {
        if (item.dataset.msHover !== 'true') return;
        const trigger = item.querySelector(':scope > [data-ms-nav-trigger]');
        let timeout;
        item.addEventListener('mouseenter', () => {
            if (window.innerWidth < 1024) return;
            clearTimeout(timeout);
            closeOtherTopLevel(item);
            item.classList.add('ms-open');
            trigger?.setAttribute('aria-expanded', 'true');
        });
        item.addEventListener('mouseleave', () => {
            if (window.innerWidth < 1024) return;
            timeout = setTimeout(() => {
                item.classList.remove('ms-open');
                trigger?.setAttribute('aria-expanded', 'false');
            }, 150);
        });
    });

    const closeAll = () => {
        document.querySelectorAll('[data-ms-nav-item].ms-open').forEach(el => {
            el.classList.remove('ms-open');
            el.querySelector(':scope > [data-ms-nav-trigger]')?.setAttribute('aria-expanded', 'false');
        });
    };

    // Capture phase — fires before any stopPropagation lower in the tree
    document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-ms-nav-trigger]');
        if (!trigger) return;
        const item = trigger.closest('[data-ms-nav-item]');
        if (!item) return;
        if (window.innerWidth >= 1024) closeOtherTopLevel(item);
        const isOpen = item.classList.contains('ms-open');
        item.classList.toggle('ms-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
    }, true);

    // Bubble phase — close all when clicking outside nav items
    document.addEventListener('click', e => {
        if (e.target.closest('[data-ms-nav-item]')) return;
        closeAll();
    });

    window.addEventListener('close-all-dropdowns', closeAll);
}

/* ── Mega menu top positioning (fixed panels aligned to header bottom) ── */
function initMegaPositioning() {
    const header = document.querySelector('[data-ms-header]');
    if (!header) return;

    const update = () => {
        const bottom = Math.round(header.getBoundingClientRect().bottom);
        document.querySelectorAll('[data-ms-mega-top]')
            .forEach(el => { el.style.top = bottom + 'px'; });
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
}

/* ── Fixed header spacer ── */
function initHeaderSpacer() {
    const wrapper = document.querySelector('.ms-nav-wrapper');
    const header = document.querySelector('[data-ms-header]');
    if (!wrapper || !header) return;

    const update = () => {
        wrapper.style.setProperty('--ms-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`);
    };

    window.addEventListener('resize', update, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(update).observe(header);
    }
    update();
}

/* ── Mobile menu (hamburger ↔ unified nav drawer) ── */
function initMobile() {
    if (document.documentElement.hasAttribute('data-ms-mobile-inited')) return;
    document.documentElement.setAttribute('data-ms-mobile-inited', '');

    const currentNav = () => document.querySelector('[data-ms-unified-nav]');

    const setOpen = open => {
        const nav = currentNav();
        if (!nav) return;
        nav.classList.toggle('ms-open', open);
        document.querySelectorAll('[data-ms-mobile-toggle]').forEach(toggle => {
            toggle.classList.toggle('ms-open', open);
            toggle.setAttribute('aria-expanded', String(open));
        });
        document.body.style.overflow = open ? 'hidden' : '';
        if (open) nav.scrollTop = 0;
    };

    // touch-action: manipulation on the button eliminates the 300ms tap delay,
    // so a plain click listener is sufficient and avoids double-fire on mobile.
    document.addEventListener('click', e => {
        if (!e.target.closest('[data-ms-mobile-toggle]')) return;
        e.preventDefault();
        setOpen(!currentNav()?.classList.contains('ms-open'));
    }, true);

    document.addEventListener('click', e => {
        if (!e.target.closest('[data-ms-mobile-close]')) return;
        e.preventDefault();
        setOpen(false);
    }, true);

    document.addEventListener('click', e => {
        const nav = currentNav();
        if (nav?.contains(e.target) && e.target.closest('a[href]')) setOpen(false);
    });

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && currentNav()?.classList.contains('ms-open')) setOpen(false);
    });
}

/* ── Scroll indicator for mega menu panels ── */
function initScrollIndicators() {
    document.querySelectorAll('[data-ms-scroll-panel]').forEach(panel => {
        const checkBottom = () => {
            panel.toggleAttribute(
                'data-at-bottom',
                panel.scrollHeight - panel.scrollTop <= panel.clientHeight + 4
            );
        };
        panel.addEventListener('scroll', checkBottom, { passive: true });
        checkBottom();
    });

    document.querySelectorAll('[data-ms-scroll-btn]').forEach(btn => {
        const panel = btn.closest('[data-ms-scroll-panel]');
        if (!panel) return;
        let iv;
        btn.addEventListener('mouseenter', () => { iv = setInterval(() => panel.scrollBy(0, 4), 16); });
        btn.addEventListener('mouseleave', () => clearInterval(iv));
    });
}

/* ── Re-register Alpine components after HTMX swaps widget content ── */
function initHtmxAlpine() {
    const pending = [];
    const origAdd = document.addEventListener.bind(document);

    // Wait until Alpine finishes its own init cycle before intercepting.
    // This ensures main-bundle alpine:init listeners (e.g. autoTranslate) run
    // through the real addEventListener and are registered before Alpine boots.
    origAdd('alpine:initialized', () => {
        document.addEventListener = function(type, listener, options) {
            if (type === 'alpine:init') { pending.push(listener); return; }
            return origAdd(type, listener, options);
        };
    }, { once: true });

    // htmx:afterSwap fires synchronously, before Alpine's MutationObserver (async microtask).
    // Replaying only the captured listeners ensures Alpine.data() is registered
    // before Alpine tries to initialize the new x-data elements.
    // Alpine.plugin is patched to a no-op during replay so that plugin re-registration
    // (e.g. alpinejs-persist) doesn't throw and abort the listener before Alpine.data() runs.
    origAdd('htmx:afterSwap', () => {
        markActive();
        if (!pending.length || typeof Alpine === 'undefined') return;
        const origPlugin = Alpine.plugin.bind(Alpine);
        Alpine.plugin = plugin => { try { origPlugin(plugin); } catch (_) {} };
        try {
            pending.splice(0).forEach(fn => { try { fn(); } catch (_) {} });
        } finally {
            Alpine.plugin = origPlugin;
        }
    });

    origAdd('htmx:oobAfterSwap', markActive);
}

function init() {
    markActive();
    initHeaderSpacer();
    initNavItems();
    initMobile();
    initMegaPositioning();
    initScrollIndicators();
    initHtmxAlpine();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
